import React, { useEffect, useState } from 'react';
import { UserPlus, Check, X, Search, Loader2, UserMinus, Users, Inbox, Send } from 'lucide-react';
import { useSubPath } from '../lib/useSubPath';
import { api, type UserSearchResult, type FriendsData } from '../lib/api';
import { Avatar } from './Avatar';

interface Props {
  myId: string;
  data: FriendsData | null;
  reload: () => Promise<void>;
  onOpenUser: (id: string) => void;
  notify: (msg: string) => void;
}

type Tab = 'friends' | 'requests' | 'add';

const TAB_SLUGS: Record<Tab, string> = { friends: 'lista', requests: 'solicitudes', add: 'agregar' };

/**
 * Amigos: la lista, las solicitudes que llegan y las que enviaste, y un
 * buscador para mandar nuevas. Solo con cuenta; los invitados no tienen dónde
 * guardar una amistad.
 */
export const FriendsView: React.FC<Props> = ({ myId, data, reload, onOpenUser, notify }) => {
  // La pestaña va en la dirección (/amigos/solicitudes…). Sin ella se abre
  // la de solicitudes si hay alguna esperando.
  const [[slug], setSub] = useSubPath('/amigos');
  const tab: Tab =
    (Object.keys(TAB_SLUGS) as Tab[]).find((t) => TAB_SLUGS[t] === slug) ??
    (data?.incoming.length ? 'requests' : 'friends');
  const setTab = (t: Tab) => setSub([TAB_SLUGS[t]], { keepScroll: true });
  const [busy, setBusy] = useState<string | null>(null);

  const act = async (id: string, run: () => Promise<unknown>, done?: string) => {
    setBusy(id);
    try {
      await run();
      await reload();
      if (done) notify(done);
    } catch (err) {
      notify(err instanceof Error ? err.message : 'No se pudo completar.');
    } finally {
      setBusy(null);
    }
  };

  const sendRequest = (id: string) =>
    act(
      id,
      async () => {
        const res = await api.post<{ accepted: boolean }>('/friends/requests', { userId: id });
        notify(res.accepted ? 'Ya sois amigos.' : 'Solicitud enviada.');
      },
    );
  const accept = (id: string) => act(id, () => api.post(`/friends/requests/${encodeURIComponent(id)}/accept`), 'Ya sois amigos.');
  const remove = (id: string, done: string) => act(id, () => api.delete(`/friends/${encodeURIComponent(id)}`), done);

  const incoming = data?.incoming ?? [];
  const outgoing = data?.outgoing ?? [];
  const friends = data?.friends ?? [];
  const online = friends.filter((f) => f.online);

  return (
    <div className="max-w-3xl mx-auto px-4 py-8 space-y-6">
      <div>
        <h1 className="text-2xl font-display font-bold flex items-center gap-2">
          <Users className="w-6 h-6 text-sky-400" />
          Amigos
        </h1>
        <p className="text-ink-400 text-sm mt-1">
          A tus amigos puedes invitarlos a tus partidas con un toque, y ves quién está conectado.
        </p>
      </div>

      <div role="tablist" className="flex border-b border-ink-700">
        <TabButton active={tab === 'friends'} onClick={() => setTab('friends')} label="Mis amigos" count={friends.length} />
        <TabButton active={tab === 'requests'} onClick={() => setTab('requests')} label="Solicitudes" count={incoming.length} highlight />
        <TabButton active={tab === 'add'} onClick={() => setTab('add')} label="Agregar" />
      </div>

      {data === null ? (
        <div className="h-24 shimmer rounded-lg" />
      ) : tab === 'friends' ? (
        friends.length === 0 ? (
          <Empty
            icon={<Users className="w-8 h-8" />}
            text="Todavía no tienes amigos aquí. Búscalos por su usuario en «Agregar»."
            action={{ label: 'Agregar amigos', onClick: () => setTab('add') }}
          />
        ) : (
          <div className="surface divide-y divide-ink-800">
            {online.length > 0 && (
              <p className="px-4 py-2 text-xs text-ink-500">
                {online.length} {online.length === 1 ? 'conectado' : 'conectados'} ahora
              </p>
            )}
            {[...friends]
              .sort((a, b) => Number(b.online) - Number(a.online))
              .map((f) => (
                <Row key={f.id} avatar={f.avatar} name={f.username} online={f.online} onOpen={() => onOpenUser(f.id)}
                  sub={f.online ? (f.inRoom ? 'Jugando una partida' : 'Conectado') : `D-Rating ${f.dRating}`}>
                  <IconButton
                    label={`Dejar de ser amigo de ${f.username}`}
                    busy={busy === f.id}
                    danger
                    onClick={() => {
                      if (window.confirm(`¿Quitar a ${f.username} de tus amigos?`)) {
                        void remove(f.id, 'Ya no sois amigos.');
                      }
                    }}
                  >
                    <UserMinus className="w-4 h-4" />
                  </IconButton>
                </Row>
              ))}
          </div>
        )
      ) : tab === 'requests' ? (
        <div className="space-y-6">
          <section className="space-y-2">
            <h2 className="text-sm font-semibold text-ink-300 flex items-center gap-1.5">
              <Inbox className="w-4 h-4" /> Recibidas
            </h2>
            {incoming.length === 0 ? (
              <p className="text-ink-500 text-sm">No tienes solicitudes pendientes.</p>
            ) : (
              <div className="surface divide-y divide-ink-800">
                {incoming.map((r) => (
                  <Row key={r.id} avatar={r.avatar} name={r.username} onOpen={() => onOpenUser(r.id)} sub="Quiere ser tu amigo">
                    <button
                      onClick={() => void accept(r.id)}
                      disabled={busy === r.id}
                      className="flex items-center gap-1.5 px-3 h-9 rounded-lg text-sm font-semibold bg-sky-600 hover:bg-sky-500 text-white disabled:opacity-50 transition-colors"
                    >
                      {busy === r.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
                      Aceptar
                    </button>
                    <IconButton label={`Rechazar a ${r.username}`} busy={false} onClick={() => void remove(r.id, 'Solicitud rechazada.')}>
                      <X className="w-4 h-4" />
                    </IconButton>
                  </Row>
                ))}
              </div>
            )}
          </section>

          <section className="space-y-2">
            <h2 className="text-sm font-semibold text-ink-300 flex items-center gap-1.5">
              <Send className="w-4 h-4" /> Enviadas
            </h2>
            {outgoing.length === 0 ? (
              <p className="text-ink-500 text-sm">No tienes solicitudes enviadas sin respuesta.</p>
            ) : (
              <div className="surface divide-y divide-ink-800">
                {outgoing.map((r) => (
                  <Row key={r.id} avatar={r.avatar} name={r.username} onOpen={() => onOpenUser(r.id)} sub="Esperando respuesta">
                    <button
                      onClick={() => void remove(r.id, 'Solicitud cancelada.')}
                      disabled={busy === r.id}
                      className="px-3 h-9 rounded-lg text-sm font-medium text-ink-400 hover:text-ink-100 hover:bg-ink-850 transition-colors"
                    >
                      Cancelar
                    </button>
                  </Row>
                ))}
              </div>
            )}
          </section>
        </div>
      ) : (
        <AddFriends myId={myId} data={data} busy={busy} onSend={(id) => void sendRequest(id)} onAccept={(id) => void accept(id)} onOpenUser={onOpenUser} />
      )}
    </div>
  );
};

const AddFriends: React.FC<{
  myId: string;
  data: FriendsData;
  busy: string | null;
  onSend: (id: string) => void;
  onAccept: (id: string) => void;
  onOpenUser: (id: string) => void;
}> = ({ myId, data, busy, onSend, onAccept, onOpenUser }) => {
  const [q, setQ] = useState('');
  const [results, setResults] = useState<UserSearchResult[] | null>(null);

  useEffect(() => {
    const term = q.trim();
    if (!term) {
      setResults(null);
      return;
    }
    const handle = window.setTimeout(async () => {
      try {
        const res = await api.get<{ users: UserSearchResult[] }>(`/users/search?q=${encodeURIComponent(term)}`);
        setResults(res.users.filter((u) => u.id !== myId));
      } catch {
        setResults([]);
      }
    }, 280);
    return () => window.clearTimeout(handle);
  }, [q, myId]);

  const status = (id: string) =>
    data.friends.some((f) => f.id === id)
      ? 'friends'
      : data.outgoing.some((r) => r.id === id)
        ? 'outgoing'
        : data.incoming.some((r) => r.id === id)
          ? 'incoming'
          : 'none';

  return (
    <div className="space-y-3">
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-ink-500 pointer-events-none" />
        <input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Busca por nombre de usuario…"
          aria-label="Buscar jugadores"
          type="search"
          autoFocus
          className="w-full h-11 pl-9 pr-3 rounded-lg bg-ink-850 border border-ink-700 text-ink-100 text-sm
                     placeholder:text-ink-500 outline-none focus:border-sky-500 focus:ring-2 focus:ring-sky-500/25"
        />
      </div>

      {results === null ? (
        <p className="text-ink-500 text-sm">Escribe el usuario de la persona que quieres agregar.</p>
      ) : results.length === 0 ? (
        <p className="text-ink-500 text-sm">Nadie con ese usuario.</p>
      ) : (
        <div className="surface divide-y divide-ink-800">
          {results.map((u) => {
            const st = status(u.id);
            return (
              <Row key={u.id} avatar={u.avatar} name={u.username} onOpen={() => onOpenUser(u.id)} sub={`D-Rating ${u.dRating}`}>
                {st === 'friends' ? (
                  <span className="chip border-success-600 text-success-400 bg-success-500/10">
                    <Check className="w-3.5 h-3.5" /> Amigos
                  </span>
                ) : st === 'outgoing' ? (
                  <span className="text-ink-500 text-sm">Solicitud enviada</span>
                ) : st === 'incoming' ? (
                  <button
                    onClick={() => onAccept(u.id)}
                    disabled={busy === u.id}
                    className="flex items-center gap-1.5 px-3 h-9 rounded-lg text-sm font-semibold bg-sky-600 hover:bg-sky-500 text-white disabled:opacity-50"
                  >
                    <Check className="w-4 h-4" /> Aceptar
                  </button>
                ) : (
                  <button
                    onClick={() => onSend(u.id)}
                    disabled={busy === u.id}
                    className="flex items-center gap-1.5 px-3 h-9 rounded-lg text-sm font-semibold bg-ember-500 hover:bg-ember-400 text-on-accent disabled:opacity-50 transition-colors"
                  >
                    {busy === u.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <UserPlus className="w-4 h-4" />}
                    Agregar
                  </button>
                )}
              </Row>
            );
          })}
        </div>
      )}
    </div>
  );
};

const Row: React.FC<{
  avatar: unknown;
  name: string;
  sub?: string;
  online?: boolean;
  onOpen: () => void;
  children?: React.ReactNode;
}> = ({ avatar, name, sub, online, onOpen, children }) => (
  <div className="flex items-center gap-3 px-4 py-3">
    <button onClick={onOpen} className="relative shrink-0" aria-label={`Ver el perfil de ${name}`}>
      <Avatar config={avatar as never} size={40} />
      {online !== undefined && (
        <span
          className={`absolute -bottom-0.5 -right-0.5 w-3.5 h-3.5 rounded-full border-2 border-ink-900 ${
            online ? 'bg-success-500' : 'bg-ink-600'
          }`}
          aria-hidden="true"
        />
      )}
    </button>
    <button onClick={onOpen} className="min-w-0 flex-1 text-left">
      <p className="font-medium text-ink-100 truncate">{name}</p>
      {sub && <p className="text-xs text-ink-500 truncate">{sub}</p>}
    </button>
    <div className="flex items-center gap-1.5 shrink-0">{children}</div>
  </div>
);

const IconButton: React.FC<{
  label: string;
  busy: boolean;
  danger?: boolean;
  onClick: () => void;
  children: React.ReactNode;
}> = ({ label, busy, danger, onClick, children }) => (
  <button
    onClick={onClick}
    disabled={busy}
    aria-label={label}
    title={label}
    className={`w-9 h-9 flex items-center justify-center rounded-lg transition-colors disabled:opacity-50 ${
      danger ? 'text-ink-500 hover:text-danger-400 hover:bg-danger-500/10' : 'text-ink-400 hover:text-ink-100 hover:bg-ink-850'
    }`}
  >
    {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : children}
  </button>
);

const TabButton: React.FC<{
  active: boolean;
  onClick: () => void;
  label: string;
  count?: number;
  highlight?: boolean;
}> = ({ active, onClick, label, count, highlight }) => (
  <button
    role="tab"
    aria-selected={active}
    onClick={onClick}
    className={`flex items-center gap-1.5 px-4 py-2.5 text-sm font-medium border-b-2 -mb-px transition-colors ${
      active ? 'border-sky-500 text-ink-50' : 'border-transparent text-ink-400 hover:text-ink-200'
    }`}
  >
    {label}
    {count !== undefined && count > 0 && (
      <span
        className={`min-w-5 h-5 px-1.5 rounded-full text-[0.6875rem] font-bold flex items-center justify-center ${
          highlight ? 'bg-ember-500 text-on-accent' : 'bg-ink-800 text-ink-300'
        }`}
      >
        {count}
      </span>
    )}
  </button>
);

const Empty: React.FC<{
  icon: React.ReactNode;
  text: string;
  action?: { label: string; onClick: () => void };
}> = ({ icon, text, action }) => (
  <div className="surface p-10 text-center space-y-3">
    <div className="text-ink-600 flex justify-center">{icon}</div>
    <p className="text-ink-400 text-sm">{text}</p>
    {action && (
      <button onClick={action.onClick} className="text-sky-400 hover:text-sky-300 font-medium text-sm">
        {action.label}
      </button>
    )}
  </div>
);
